import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  IconButton,
  Divider,
  Chip,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CustomButton from "../../Button/CustomButton";
import SaveIcon from "@mui/icons-material/Save";
import AddIcon from "@mui/icons-material/Add";
import CodeIcon from "@mui/icons-material/Code";

const CustomToolModal = ({ open, onClose, data, onSave }) => {
  const isEdit = !!data;

  const [formData, setFormData] = useState({
    name: "",
    description: "",
    category: "API",
    method: "GET",
    endpoint: "",
    authentication: "API Key",
    apiKey: "",
    headers: [],
    parameters: [],
    responseMapping: "",
    timeout: 30000,
    retryAttempts: 3,
    errorHandling: "Return Error Message",
  });

  const [newHeader, setNewHeader] = useState({ key: "", value: "" });
  const [newParameter, setNewParameter] = useState({ name: "", type: "string", required: true, description: "" });

  useEffect(() => {
    if (data) {
      setFormData({
        name: data.name || "",
        description: data.description || "",
        category: data.category || "API",
        method: data.method || "GET",
        endpoint: data.endpoint || "",
        authentication: data.authentication || "API Key",
        apiKey: data.apiKey || "",
        headers: data.headers || [],
        parameters: data.parameters || [],
        responseMapping: data.responseMapping || "",
        timeout: data.timeout || 30000,
        retryAttempts: data.retryAttempts || 3,
        errorHandling: data.errorHandling || "Return Error Message",
      });
    }
  }, [data]);

  const handleChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  const handleAddHeader = () => {
    if (newHeader.key.trim() && newHeader.value.trim()) {
      setFormData({
        ...formData,
        headers: [...formData.headers, { ...newHeader }],
      });
      setNewHeader({ key: "", value: "" });
    }
  };

  const handleRemoveHeader = (index) => {
    setFormData({
      ...formData,
      headers: formData.headers.filter((_, i) => i !== index),
    });
  };

  const handleAddParameter = () => {
    if (newParameter.name.trim()) {
      setFormData({
        ...formData,
        parameters: [...formData.parameters, { ...newParameter }],
      });
      setNewParameter({ name: "", type: "string", required: true, description: "" });
    }
  };

  const handleRemoveParameter = (index) => {
    setFormData({
      ...formData,
      parameters: formData.parameters.filter((_, i) => i !== index),
    });
  };

  const handleSubmit = () => {
    onSave(formData);
    handleClose();
  };

  const handleClose = () => {
    setFormData({
      name: "",
      description: "",
      category: "API",
      method: "GET",
      endpoint: "",
      authentication: "API Key",
      apiKey: "",
      headers: [],
      parameters: [],
      responseMapping: "",
      timeout: 30000,
      retryAttempts: 3,
      errorHandling: "Return Error Message",
    });
    setNewHeader({ key: "", value: "" });
    setNewParameter({ name: "", type: "string", required: true, description: "" });
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Typography variant="h5" fontWeight={600}>
            {isEdit ? "Edit Custom Tool" : "Create Custom Tool"}
          </Typography>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
          {/* Alert */}
          <Alert severity="info">
            Create custom tools to integrate external APIs and services with your AI assistants.
          </Alert>

          {/* Basic Information */}
          <Box>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              Tool Information
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 2 }}>
              <TextField
                fullWidth
                label="Tool Name"
                value={formData.name}
                onChange={(e) => handleChange("name", e.target.value)}
                required
                placeholder="e.g., CRM Customer Lookup"
              />
              <TextField
                fullWidth
                label="Description"
                value={formData.description}
                onChange={(e) => handleChange("description", e.target.value)}
                multiline
                rows={2}
                placeholder="What does this tool do?"
              />
              <FormControl fullWidth>
                <InputLabel>Category</InputLabel>
                <Select
                  value={formData.category}
                  label="Category"
                  onChange={(e) => handleChange("category", e.target.value)}
                >
                  <MenuItem value="API">API Integration</MenuItem>
                  <MenuItem value="Database">Database Query</MenuItem>
                  <MenuItem value="Webhook">Webhook</MenuItem>
                  <MenuItem value="Function">Custom Function</MenuItem>
                  <MenuItem value="CRM">CRM Integration</MenuItem>
                  <MenuItem value="Calendar">Calendar</MenuItem>
                  <MenuItem value="Payment">Payment Processing</MenuItem>
                  <MenuItem value="Other">Other</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </Box>

          <Divider />

          {/* API Configuration */}
          <Box>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              API Configuration
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 2 }}>
              <Box sx={{ display: "flex", gap: 2 }}>
                <FormControl sx={{ width: 150 }}>
                  <InputLabel>Method</InputLabel>
                  <Select
                    value={formData.method}
                    label="Method"
                    onChange={(e) => handleChange("method", e.target.value)}
                  >
                    <MenuItem value="GET">GET</MenuItem>
                    <MenuItem value="POST">POST</MenuItem>
                    <MenuItem value="PUT">PUT</MenuItem>
                    <MenuItem value="PATCH">PATCH</MenuItem>
                    <MenuItem value="DELETE">DELETE</MenuItem>
                  </Select>
                </FormControl>
                <TextField
                  fullWidth
                  label="API Endpoint"
                  value={formData.endpoint}
                  onChange={(e) => handleChange("endpoint", e.target.value)}
                  placeholder="https://api.example.com/v1/resource"
                />
              </Box>

              <FormControl fullWidth>
                <InputLabel>Authentication</InputLabel>
                <Select
                  value={formData.authentication}
                  label="Authentication"
                  onChange={(e) => handleChange("authentication", e.target.value)}
                >
                  <MenuItem value="API Key">API Key</MenuItem>
                  <MenuItem value="Bearer Token">Bearer Token</MenuItem>
                  <MenuItem value="Basic Auth">Basic Auth</MenuItem>
                  <MenuItem value="OAuth 2.0">OAuth 2.0</MenuItem>
                  <MenuItem value="None">None</MenuItem>
                </Select>
              </FormControl>

              {formData.authentication !== "None" && (
                <TextField
                  fullWidth
                  label="API Key / Token"
                  value={formData.apiKey}
                  onChange={(e) => handleChange("apiKey", e.target.value)}
                  type="password"
                  placeholder="Enter your API key or token"
                />
              )}
            </Box>
          </Box>

          <Divider />

          {/* Headers */}
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <CodeIcon color="primary" />
                <Typography variant="h6" fontWeight={600}>
                  Custom Headers ({formData.headers.length})
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <Box sx={{ display: "flex", gap: 1 }}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Header Key"
                    value={newHeader.key}
                    onChange={(e) => setNewHeader({ ...newHeader, key: e.target.value })}
                    placeholder="Content-Type"
                  />
                  <TextField
                    fullWidth
                    size="small"
                    label="Header Value"
                    value={newHeader.value}
                    onChange={(e) => setNewHeader({ ...newHeader, value: e.target.value })}
                    placeholder="application/json"
                  />
                  <CustomButton
                    icon={<AddIcon />}
                    iconPosition="left"
                    text="Add"
                    onClick={handleAddHeader}
                    disabled={!newHeader.key.trim() || !newHeader.value.trim()}
                    size="small"
                  />
                </Box>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  {formData.headers.map((header, idx) => (
                    <Box
                      key={idx}
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        p: 1,
                        backgroundColor: "#F9FAFB",
                        borderRadius: 1,
                      }}
                    >
                      <Typography variant="body2">
                        <strong>{header.key}:</strong> {header.value}
                      </Typography>
                      <IconButton size="small" onClick={() => handleRemoveHeader(idx)}>
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  ))}
                </Box>
              </Box>
            </AccordionDetails>
          </Accordion>

          {/* Parameters */}
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <CodeIcon color="primary" />
                <Typography variant="h6" fontWeight={600}>
                  Parameters ({formData.parameters.length})
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Parameter Name"
                    value={newParameter.name}
                    onChange={(e) => setNewParameter({ ...newParameter, name: e.target.value })}
                    placeholder="customer_id"
                  />
                  <Box sx={{ display: "flex", gap: 1 }}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Type</InputLabel>
                      <Select
                        value={newParameter.type}
                        label="Type"
                        onChange={(e) => setNewParameter({ ...newParameter, type: e.target.value })}
                      >
                        <MenuItem value="string">String</MenuItem>
                        <MenuItem value="number">Number</MenuItem>
                        <MenuItem value="boolean">Boolean</MenuItem>
                        <MenuItem value="object">Object</MenuItem>
                        <MenuItem value="array">Array</MenuItem>
                      </Select>
                    </FormControl>
                    <FormControl fullWidth size="small">
                      <InputLabel>Required</InputLabel>
                      <Select
                        value={newParameter.required}
                        label="Required"
                        onChange={(e) => setNewParameter({ ...newParameter, required: e.target.value })}
                      >
                        <MenuItem value={true}>Required</MenuItem>
                        <MenuItem value={false}>Optional</MenuItem>
                      </Select>
                    </FormControl>
                  </Box>
                  <TextField
                    fullWidth
                    size="small"
                    label="Description"
                    value={newParameter.description}
                    onChange={(e) => setNewParameter({ ...newParameter, description: e.target.value })}
                    placeholder="What is this parameter used for?"
                  />
                  <CustomButton
                    icon={<AddIcon />}
                    iconPosition="left"
                    text="Add Parameter"
                    onClick={handleAddParameter}
                    disabled={!newParameter.name.trim()}
                    size="small"
                  />
                </Box>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  {formData.parameters.map((param, idx) => (
                    <Box
                      key={idx}
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                        p: 1.5,
                        backgroundColor: "#F9FAFB",
                        borderRadius: 1,
                      }}
                    >
                      <Box sx={{ flex: 1 }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                          <Typography variant="body2" fontWeight={600}>
                            {param.name}
                          </Typography>
                          <Chip label={param.type} size="small" />
                          {param.required && <Chip label="Required" size="small" color="error" />}
                        </Box>
                        {param.description && (
                          <Typography variant="caption" color="text.secondary">
                            {param.description}
                          </Typography>
                        )}
                      </Box>
                      <IconButton size="small" onClick={() => handleRemoveParameter(idx)}>
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  ))}
                </Box>
              </Box>
            </AccordionDetails>
          </Accordion>

          <Divider />

          {/* Advanced Settings */}
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h6" fontWeight={600}>
                Advanced Settings
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <TextField
                  fullWidth
                  label="Response Mapping (JSONPath)"
                  value={formData.responseMapping}
                  onChange={(e) => handleChange("responseMapping", e.target.value)}
                  placeholder="$.data.customer.name"
                  helperText="Extract specific data from API response"
                />
                <Box sx={{ display: "flex", gap: 2 }}>
                  <TextField
                    fullWidth
                    label="Timeout (ms)"
                    type="number"
                    value={formData.timeout}
                    onChange={(e) => handleChange("timeout", parseInt(e.target.value))}
                    InputProps={{ inputProps: { min: 1000, max: 60000 } }}
                  />
                  <TextField
                    fullWidth
                    label="Retry Attempts"
                    type="number"
                    value={formData.retryAttempts}
                    onChange={(e) => handleChange("retryAttempts", parseInt(e.target.value))}
                    InputProps={{ inputProps: { min: 0, max: 5 } }}
                  />
                </Box>
                <FormControl fullWidth>
                  <InputLabel>Error Handling</InputLabel>
                  <Select
                    value={formData.errorHandling}
                    label="Error Handling"
                    onChange={(e) => handleChange("errorHandling", e.target.value)}
                  >
                    <MenuItem value="Return Error Message">Return Error Message</MenuItem>
                    <MenuItem value="Use Fallback">Use Fallback Response</MenuItem>
                    <MenuItem value="Retry">Auto Retry</MenuItem>
                    <MenuItem value="Skip">Skip Silently</MenuItem>
                  </Select>
                </FormControl>
              </Box>
            </AccordionDetails>
          </Accordion>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2, gap: 1 }}>
        <CustomButton text="Test Tool" variant="outlined" onClick={() => alert("Testing tool...")} />
        <Box sx={{ flex: 1 }} />
        <CustomButton text="Cancel" variant="outlined" onClick={handleClose} />
        <CustomButton
          icon={<SaveIcon />}
          iconPosition="left"
          text={isEdit ? "Update Tool" : "Create Tool"}
          onClick={handleSubmit}
          disabled={!formData.name.trim() || !formData.endpoint.trim()}
        />
      </DialogActions>
    </Dialog>
  );
};

export default CustomToolModal;
